import { useRef, useState } from "react";
import { ImagePlus, Link2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { mediaUrl } from "@/components/home/ReelBanner";

const head = "font-mono text-[11px] uppercase tracking-[0.25em] text-[#1c1418]/70";

const PhotoSet = ({ kind, title, hint, items, locked, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post(`/account/files?kind=${kind}`, fd); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
    setBusy(false);
    onChange();
  };
  const remove = async (id) => { await api.delete(`/account/files/${id}`); onChange(); };
  return (
    <div data-testid={`account-${kind}-set`}>
      <p className={head}>{title}</p>
      <p className="mt-1 text-sm text-[#1c1418]/70">{hint}</p>
      <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
        {items.map((p, i) => (
          <div key={p.id} className="relative aspect-[3/4] bg-[#f4f4f4]">
            <img src={mediaUrl(p.url)} alt={`${title} ${i + 1}`} className="h-full w-full object-cover" />
            {!locked && <button type="button" onClick={() => remove(p.id)} data-testid={`account-${kind}-remove-${i}`} aria-label="Rimuovi"
              className="absolute right-1 top-1 grid h-7 w-7 place-items-center bg-white/90"><X className="h-4 w-4" /></button>}
          </div>
        ))}
        {!locked && (
          <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid={`account-${kind}-add`}
            className="grid aspect-[3/4] place-items-center border border-dashed border-[#1c1418]/40 transition-colors hover:bg-[#f4f4f4] disabled:opacity-60">
            <span className="flex flex-col items-center gap-1 font-mono text-[11px] uppercase"><ImagePlus className="h-5 w-5 text-[#e9458f]" />{busy ? "…" : "Aggiungi"}</span>
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/*" multiple onChange={add} className="hidden" data-testid={`account-${kind}-input`} />
    </div>
  );
};

const Links = ({ initial, locked }) => {
  const [links, setLinks] = useState(initial.length ? initial : [""]);
  const save = async (next) => {
    try { await api.put("/account/links", { links: next }); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const set = (i, v) => setLinks((l) => l.map((x, j) => (j === i ? v : x)));
  return (
    <div data-testid="account-links">
      <p className={head}>Portfolio · link</p>
      <p className="mt-1 text-sm text-[#1c1418]/70">Link al tuo book online (Google Drive, Dropbox, sito, Instagram…).</p>
      <div className="mt-3 space-y-2">
        {links.map((l, i) => (
          <div key={i} className="flex items-center gap-2 border-b border-[#1c1418]/30">
            <Link2 className="h-4 w-4 shrink-0 text-[#e9458f]" />
            <input value={l} disabled={locked} placeholder="https://" onChange={(e) => set(i, e.target.value)} onBlur={() => save(links)}
              data-testid={`account-link-input-${i}`} className="w-full bg-transparent py-3 outline-none" />
          </div>
        ))}
      </div>
      {!locked && links.length < 10 && (
        <button type="button" onClick={() => setLinks((l) => [...l, ""])} data-testid="account-link-add" className="mt-3 flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] underline">
          <Plus className="h-3.5 w-3.5" /> Altro link
        </button>
      )}
    </div>
  );
};

export const Materials = ({ data, reload }) => {
  const locked = !!data.submitted_at;
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/account/submit"); toast.success("Materiale inviato"); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <section data-testid="account-materials" className="space-y-8 border border-[#1c1418]/20 p-5 md:p-8">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-display text-3xl uppercase md:text-4xl">01 · Snaps & portfolio</h2>
        {locked && <span data-testid="account-materials-sent" className="bg-[#1c1418] px-2 py-1 font-mono text-[11px] uppercase text-white">Inviato</span>}
      </div>
      <PhotoSet kind="snap" title="Snaps · polaroid" hint="Almeno una: viso e figura intera, luce naturale, senza trucco." items={data.snaps} locked={locked} onChange={reload} />
      <Links initial={data.links} locked={locked} />
      <PhotoSet kind="photo" title="Oppure allega alcune foto" hint="Se non hai un link al portfolio, allega qui alcune foto del tuo book." items={data.photos} locked={locked} onChange={reload} />
      {!locked && (
        <button type="button" disabled={busy} onClick={submit} data-testid="account-materials-submit"
          className="w-full bg-[#1c1418] py-4 font-mono text-xs uppercase tracking-[0.25em] text-white disabled:opacity-60">
          {busy ? "Invio…" : "Invia e passa ai contratti"}
        </button>
      )}
    </section>
  );
};
